import { useEffect, useContext, useState, useRef, useCallback } from 'react';
import { AlertCircle, XCircle, RefreshCw, Check, MoreVertical, UserPlus } from 'lucide-react';
import { useMetaAds } from 'hooks/useMetaAds';
import { AuthContext } from 'components/context/AuthContext';
import { useAds } from 'components/context/AdsContext';
import { MetaIcon } from 'components';
import { logApi } from 'api';
import { toast } from 'react-hot-toast';
import MetaDropdowns from './MetaDropdowns';
import MetaPermissionInlineNotice from './MetaPermissionInlineNotice';

const { createLog } = logApi;

export const MetaAdsSection = () => {
  const {
    isConnected,
    loadingAccounts,
    accounts,
    metaAccountsPermissionError,
    startMetaOAuth,
    disconnect,
    fetchMetaAccounts,
    updateMetaConnectionData
  } = useMetaAds();

  const { auth } = useContext(AuthContext);
  const { metaUserInfo } = useAds();
  const activeId = auth.user?.meta_ad_account_id || null;

  const [showMenu, setShowMenu] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [sortedAccounts, setSortedAccounts] = useState([]);
  const menuRef = useRef(null);

  const [selectedAccountId, setSelectedAccountId] = useState('');

  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const fetchMetaAccountsData = useCallback(async (force = false) => {
    try {
      await fetchMetaAccounts(force);
    } catch (err) {
      console.error('Error fetching accounts:', err);
      toast.error(err?.message || 'Error al obtener cuentas');
    }
  }, [fetchMetaAccounts]);

  useEffect(() => {
    if (!isConnected) return;
    fetchMetaAccountsData();
  }, [isConnected, fetchMetaAccountsData]);  

  const handleDisconnect = async (startOAuth = false) => {
    setDisconnecting(true);
    try {
      await disconnect();
      if (startOAuth) {
        setTimeout(() => {
          startMetaOAuth(true);
        }, 300);
      }
    } finally {
      setDisconnecting(false);
      setShowMenu(false);
    }
  };
  
  useEffect(() => {
    if (accounts.length && sortedAccounts.length === 0) {
      const sorted = [...accounts].sort((a, b) => {
        if (a.account_id === activeId) return -1;
        if (b.account_id === activeId) return 1;
        return 0;
      });
      setSortedAccounts(sorted);
    }
  }, [accounts, activeId, sortedAccounts.length]);   

  const activeAccount = accounts?.find(acc => acc.account_id === activeId);

  // Handle click outside of menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  // Initialize selections from current user data - only after accounts are loaded
  useEffect(() => {
    if (auth.user && accounts.length > 0) {
      setSelectedAccountId(auth.user.meta_ad_account_id || '');
    }
  }, [auth.user, accounts.length]);

  // Check for changes when selections change
  useEffect(() => {
    const currentAccount = auth.user?.meta_ad_account_id;
    
    const hasAccountChanged = selectedAccountId && selectedAccountId !== currentAccount;
    
    setHasChanges(hasAccountChanged);
  }, [selectedAccountId, auth.user]);

  const handleSave = async () => {
    if (!selectedAccountId) return;
    
    setIsSaving(true);
    try {
      await updateMetaConnectionData({
        accountId: selectedAccountId,
      });
      
      toast.success('Configuración de Meta guardada exitosamente');
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving Meta configuration:', error);
      toast.error('Error al guardar la configuración de Meta');
    } finally {
      setIsSaving(false);
    }
  };

  // Callback functions for MetaDropdowns
  const handleAccountSelect = (account) => {
    setSelectedAccountId(account.account_id);
  };

  return (
    <>
      {!auth.user ? (
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin text-white/50 mr-2" />
          <span className="text-white/80">Cargando...</span>
        </div>
      ) : (
        <section className="relative">
          {/* Header Section - Compact */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-3 flex-1">
              {/* Logo */}
              <div className="bg-blue-500/20 border border-blue-400/30 p-2 rounded-xl flex-shrink-0 flex items-center justify-center backdrop-blur-md">
                <MetaIcon width={24} height={24} />
              </div>
              
              {/* Title and Status */}
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-bold text-white mb-1">Meta Ads</h3>
                {isConnected && !loadingAccounts && (
                  activeAccount ? (
                    <span className="flex items-center justify-center text-green-300 bg-green-500/20 border border-green-400/30 px-2 py-1 rounded-lg text-xs backdrop-blur-md w-fit">
                      <Check className="h-3 w-3 mr-1" /> Conectado
                    </span>
                  ) : (
                    <span className="flex items-center justify-center text-yellow-300 bg-yellow-500/20 border border-yellow-400/30 px-2 py-1 rounded-lg text-xs backdrop-blur-md w-fit">
                      <AlertCircle className="h-3 w-3 mr-1" /> Seleccioná tu cuenta
                    </span>
                  )
                )}
                {!isConnected && (
                  <p className="text-xs text-white/60">Conecta tu cuenta de Meta Ads</p>
                )}
                {isConnected && metaUserInfo?.email && (
                  <p className="text-xs text-white/60 mt-1 truncate">{metaUserInfo.email}</p>
                )}
              </div>
            </div>

            {/* Actions Menu */}
            {isConnected && (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-2 text-white/70 hover:text-white rounded-lg hover:bg-white/10 backdrop-blur-sm transition-all flex-shrink-0"
                  aria-label="Acciones de cuenta Meta"
                >
                  <MoreVertical className="h-5 w-5" />
                </button>
                {showMenu && (
                  <div className="absolute right-0 mt-2 w-56 backdrop-blur-xl bg-white/15 border border-white/30 rounded-xl shadow-lg py-2 z-10">
                    <button
                      onClick={() => fetchMetaAccountsData(true)}
                      disabled={loadingAccounts}
                      className="flex w-full items-center px-4 py-2 text-sm text-white hover:bg-white/20 disabled:opacity-50 transition-all"
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${loadingAccounts ? 'animate-spin' : ''}`} />
                      Refrescar cuentas
                    </button>
                    <button
                      onClick={() => handleDisconnect(true)}
                      disabled={disconnecting}
                      className="flex w-full items-center px-4 py-2 text-sm text-white hover:bg-white/20 disabled:opacity-50 transition-all"
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Usar otra cuenta
                    </button>
                    <button
                      onClick={() => handleDisconnect()}
                      disabled={disconnecting}
                      className="flex w-full items-center px-4 py-2 text-sm text-red-300 hover:bg-red-500/20 disabled:opacity-50 transition-all"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      {disconnecting ? 'Desconectando...' : 'Desconectar'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* LOADING */}
          {isConnected && loadingAccounts && (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-white/50 mr-2" />
              <span className="text-white/80">Cargando cuentas...</span>
            </div>
          )}

          {isConnected && !loadingAccounts && metaAccountsPermissionError && (
            <div className="mb-6">
              <MetaPermissionInlineNotice
                message={metaAccountsPermissionError.message}
                missingPermissions={metaAccountsPermissionError.missingPermissions || []}
                onReconnect={() => startMetaOAuth(true)}
              />
            </div>
          )}

          {/* ACCOUNTS LIST */}
          {isConnected && !loadingAccounts && accounts.length > 0 && (
            <div className="space-y-6">
              <MetaDropdowns
                accounts={accounts}
                selectedAccountId={selectedAccountId}
                onAccountSelect={handleAccountSelect}
                disabled={isSaving}
                showOnlyAccount={true}
              />
            </div>
          )}

          {/* NO ACCOUNTS */}
          {isConnected && !loadingAccounts && accounts.length === 0 && !metaAccountsPermissionError && (
            <div className="flex flex-col items-center py-8 text-center">
              <div className="flex flex-col items-center gap-2 max-w-md">
                <p className="text-white/80 text-sm">No hay cuentas disponibles de Meta Ads</p>
                <p
                  className="underline text-sm cursor-pointer hover:text-white transition-colors text-white/70"
                  onClick={() => handleDisconnect(true)}
                >
                  ¿Prefieres usar otra cuenta de Meta?
                </p>
              </div>
            </div>
          )}

          {/* Save Button */}
          {isConnected && !loadingAccounts && accounts.length > 0 && (
            <div className="flex justify-center mt-6">
              <button
                onClick={handleSave}
                disabled={!hasChanges || !selectedAccountId || isSaving}
                className={`px-6 py-3 rounded-xl transition-all duration-300 flex items-center gap-2 
                  text-sm font-medium min-w-[140px] justify-center
                  ${!hasChanges || !selectedAccountId || isSaving
                    ? 'bg-white/10 text-white/50 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl'
                  }`}
              >
                {isSaving ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Guardando...</span>
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Guardar</span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* CONNECT BUTTON */}
          {!isConnected && (
            <button
              onClick={() => {
                createLog({
                  level: 'info',
                  message: 'Click en Conectar con Meta Ads'
                });
                startMetaOAuth(false);
              }}
              className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg flex items-center justify-center gap-2 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95"
            >
              <MetaIcon width={20} height={20} />
              <span>Conectar con Meta Ads</span>
            </button>
          )}

        </section>
      )}
    </>
  );
};
